import { useState } from 'react';
import { ConsolePage, Icon, Modal, Panel, QueryState, ListSkeleton, useToast } from '@/components';
import {
  useOrg,
  useCreateDept,
  useUpdateDept,
  useDeleteDept,
  useCreateUnit,
  useUpdateUnit,
  useDeleteUnit,
} from '@/hooks/queries';
import type { OrgNode } from '@/types/domain';

type DeptModal = { mode: 'add' } | { mode: 'edit'; node: OrgNode };
type UnitModal = { deptId: string; deptName: string; unitId?: string; name?: string };
type Confirm = { kind: 'dept' | 'unit'; id: string; name: string };

export function AdminOrg() {
  const { toast } = useToast();
  const orgQuery = useOrg();
  const tree = orgQuery.data ?? [];
  const createDept = useCreateDept();
  const updateDept = useUpdateDept();
  const deleteDept = useDeleteDept();
  const createUnit = useCreateUnit();
  const updateUnit = useUpdateUnit();
  const deleteUnit = useDeleteUnit();

  const [deptModal, setDeptModal] = useState<DeptModal | null>(null);
  const [unitModal, setUnitModal] = useState<UnitModal | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  return (
    <ConsolePage
      title="Organization"
      th="โครงสร้างองค์กร"
      crumbs="PEOPLE"
      maxWidth={1000}
      actions={<button className="btn btn-primary btn-sm" onClick={() => setDeptModal({ mode: 'add' })}><Icon name="plus" size={15} /> Add department</button>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 8, padding: '12px 14px', background: 'var(--sage-tint)', borderRadius: 10, fontSize: 13, color: 'var(--sage-deep)' }}>
          <Icon name="bldg" size={16} style={{ flex: 'none', marginTop: 1 }} />
          <span>Departments and their wards. A department with <strong>no wards</strong> is a central unit (e.g. องค์การพยาบาล). These are the choices staff pick from at sign-up and on invite links.</span>
        </div>

        <QueryState
          query={orgQuery}
          skeleton={<ListSkeleton rows={4} />}
          isEmpty={() => tree.length === 0}
          emptyIcon="bldg"
          emptyTitle="No departments yet"
          emptyHint="Add your hospital's departments and wards to get started."
          emptyAction={<button className="btn btn-primary btn-sm" onClick={() => setDeptModal({ mode: 'add' })}><Icon name="plus" size={15} /> Add department</button>}
        >
          {() => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {tree.map((d) => (
                <Panel key={d.deptId} pad={false}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 18px' }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Icon name="layers2" size={18} style={{ color: 'var(--sage)' }} />
                      <div>
                        <div style={{ fontSize: 14.5, fontWeight: 600 }}>{d.dept}</div>
                        <div className="th muted" style={{ fontSize: 12 }}>{d.th || '—'}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setUnitModal({ deptId: d.deptId, deptName: d.dept })}><Icon name="plus" size={14} /> Ward</button>
                      <button className="btn btn-ghost btn-sm" aria-label="Edit department" onClick={() => setDeptModal({ mode: 'edit', node: d })}><Icon name="edit" size={14} /></button>
                      <button className="btn btn-ghost btn-sm" aria-label="Delete department" style={{ color: 'var(--danger)' }} onClick={() => setConfirm({ kind: 'dept', id: d.deptId, name: d.dept })}><Icon name="trash" size={14} /></button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 18px 16px' }}>
                    {d.units.length === 0 && <span className="muted" style={{ fontSize: 12.5 }}>Central · no wards</span>}
                    {d.units.map((u) => (
                      <span key={u.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, padding: '6px 8px 6px 12px', background: 'var(--card-2)', border: '1px solid var(--line)', borderRadius: 8 }}>
                        <Icon name="pin" size={13} style={{ color: 'var(--ink-3)' }} />
                        <button onClick={() => setUnitModal({ deptId: d.deptId, deptName: d.dept, unitId: u.id, name: u.name })} style={{ all: 'unset', cursor: 'pointer' }}>{u.name}</button>
                        <button aria-label={`Remove ${u.name}`} onClick={() => setConfirm({ kind: 'unit', id: u.id, name: u.name })} style={{ all: 'unset', cursor: 'pointer', color: 'var(--ink-3)', display: 'flex', padding: 2 }}><Icon name="x" size={13} /></button>
                      </span>
                    ))}
                  </div>
                </Panel>
              ))}
            </div>
          )}
        </QueryState>
      </div>

      <Modal open={!!deptModal} onClose={() => setDeptModal(null)} title={deptModal?.mode === 'edit' ? 'Edit department' : 'Add department'} width={420}>
        {deptModal && (
          <DeptForm
            initial={deptModal.mode === 'edit' ? deptModal.node : undefined}
            pending={createDept.isPending || updateDept.isPending}
            onSubmit={(nameEn, nameTh) => {
              if (deptModal.mode === 'edit') {
                updateDept.mutate({ id: deptModal.node.deptId, patch: { nameEn, nameTh } }, { onSuccess: () => { toast('Department updated', { icon: 'check' }); setDeptModal(null); } });
              } else {
                createDept.mutate({ nameEn, nameTh }, { onSuccess: () => { toast('Department added', { icon: 'check' }); setDeptModal(null); } });
              }
            }}
          />
        )}
      </Modal>

      <Modal open={!!unitModal} onClose={() => setUnitModal(null)} title={unitModal?.unitId ? 'Rename ward' : `Add ward · ${unitModal?.deptName ?? ''}`} width={380}>
        {unitModal && (
          <UnitForm
            initialName={unitModal.name}
            pending={createUnit.isPending || updateUnit.isPending}
            onSubmit={(name) => {
              if (unitModal.unitId) {
                updateUnit.mutate({ id: unitModal.unitId, name }, { onSuccess: () => { toast('Ward renamed', { icon: 'check' }); setUnitModal(null); } });
              } else {
                createUnit.mutate({ deptId: unitModal.deptId, name }, { onSuccess: () => { toast('Ward added', { icon: 'check' }); setUnitModal(null); } });
              }
            }}
          />
        )}
      </Modal>

      <Modal open={!!confirm} onClose={() => setConfirm(null)} title="Remove?" width={380}>
        {confirm && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55 }}>
              Remove <strong>{confirm.name}</strong>{confirm.kind === 'dept' ? ' and all its wards' : ''}? Anyone currently assigned will need a new {confirm.kind === 'dept' ? 'department' : 'ward'}.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)' }}
                disabled={deleteDept.isPending || deleteUnit.isPending}
                onClick={() => {
                  const done = () => { toast('Removed', { tone: 'clay', icon: 'x' }); setConfirm(null); };
                  if (confirm.kind === 'dept') deleteDept.mutate(confirm.id, { onSuccess: done });
                  else deleteUnit.mutate(confirm.id, { onSuccess: done });
                }}
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </Modal>
    </ConsolePage>
  );
}

function DeptForm({ initial, onSubmit, pending }: { initial?: OrgNode; onSubmit: (nameEn: string, nameTh: string) => void; pending: boolean }) {
  const [nameEn, setNameEn] = useState(initial?.dept ?? '');
  const [nameTh, setNameTh] = useState(initial?.th ?? '');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <label className="field">
        <span className="label">Name</span>
        <input className="input" value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="e.g. ED · ICU · IPD · OPD" />
      </label>
      <label className="field">
        <span className="label">Thai name · ชื่อภาษาไทย</span>
        <input className="input th" value={nameTh} onChange={(e) => setNameTh(e.target.value)} placeholder="เช่น ห้องฉุกเฉิน (เว้นว่างได้)" />
      </label>
      <button className="btn btn-primary btn-block" disabled={pending || !nameEn.trim()} onClick={() => onSubmit(nameEn.trim(), nameTh.trim())}>
        {pending ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function UnitForm({ initialName, onSubmit, pending }: { initialName?: string; onSubmit: (name: string) => void; pending: boolean }) {
  const [name, setName] = useState(initialName ?? '');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <label className="field">
        <span className="label">Ward name</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. หญิง · ชาย · NCD · EYE" />
      </label>
      <button className="btn btn-primary btn-block" disabled={pending || !name.trim()} onClick={() => onSubmit(name.trim())}>
        {pending ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}
